import { graphqlHttpUrl } from "./config";

const K_VERIFIER = "toko_umag_oauth_code_verifier";

function oauthTokenUrl(): string {
  const explicit = import.meta.env.VITE_TOKO_OAUTH_TOKEN_URL?.trim();
  if (explicit) return explicit.replace(/\/$/, "");
  if (import.meta.env.DEV && typeof window !== "undefined") {
    return `${window.location.origin}/oauth/token`;
  }
  const authorize = import.meta.env.VITE_TOKO_OAUTH_AUTHORIZE_URL?.trim();
  if (authorize) {
    return authorize.replace(/\/authorize\/?$/, "/token");
  }
  try {
    return `${new URL(graphqlHttpUrl()).origin}/oauth/token`;
  } catch {
    return "http://localhost:8008/oauth/token";
  }
}
const K_STATE = "toko_umag_oauth_state";
const K_ACCESS = "toko_umag_oauth_access_token";
const K_REFRESH = "toko_umag_oauth_refresh_token";
const K_ACCESS_EXPIRES_AT = "toko_umag_oauth_access_expires_at_ms";
const K_RETURN = "toko_umag_oauth_return_path";

/** Refresh a bit before expiry so mid-shift GraphQL does not 401. */
const SKEW_MS = 60_000;

let refreshInflight: Promise<string> | null = null;
let refreshTimer: number | null = null;

export function saveOAuthPkce(verifier: string, state: string): void {
  sessionStorage.setItem(K_VERIFIER, verifier);
  sessionStorage.setItem(K_STATE, state);
}

export function readOAuthPkce(): { verifier: string; state: string } | null {
  const verifier = sessionStorage.getItem(K_VERIFIER);
  const state = sessionStorage.getItem(K_STATE);
  if (!verifier || !state) return null;
  return { verifier, state };
}

export function clearOAuthPkce(): void {
  sessionStorage.removeItem(K_VERIFIER);
  sessionStorage.removeItem(K_STATE);
}

export function persistOAuthTokenResponse(body: {
  access_token: string;
  expires_in?: number;
  refresh_token?: string | null;
}): void {
  const access = body.access_token.trim();
  sessionStorage.setItem(K_ACCESS, access);
  window.__TOKO_POS_EMBED_BEARER__ = access;
  const rt = body.refresh_token?.trim();
  if (rt) sessionStorage.setItem(K_REFRESH, rt);
  const ttl = Number(body.expires_in);
  if (Number.isFinite(ttl) && ttl > 0) {
    sessionStorage.setItem(K_ACCESS_EXPIRES_AT, String(Date.now() + Math.floor(ttl * 1000)));
  } else {
    sessionStorage.removeItem(K_ACCESS_EXPIRES_AT);
  }
  scheduleOAuthRefresh();
  // Best-effort: push fresh OAuth into Store Node for continuous cloud sync.
  void import("@/common/domain/cloudSyncBridge")
    .then((m) => m.startContinuousCloudSync())
    .catch(() => undefined);
}

function readStoredAccess(): string {
  return sessionStorage.getItem(K_ACCESS)?.trim() ?? "";
}

function accessExpiresAtMs(): number {
  const exp = Number(sessionStorage.getItem(K_ACCESS_EXPIRES_AT));
  return Number.isFinite(exp) && exp > 0 ? exp : 0;
}

/** Raw access token if still valid (with skew). Does not refresh. */
export function getOAuthAccessToken(): string {
  const stored = readStoredAccess();
  if (!stored) return "";
  const exp = accessExpiresAtMs();
  if (exp > 0 && Date.now() > exp - 15_000) {
    return "";
  }
  return stored;
}

export function getOAuthRefreshToken(): string {
  return sessionStorage.getItem(K_REFRESH)?.trim() ?? "";
}

async function refreshOAuthAccessToken(): Promise<string> {
  const refresh = getOAuthRefreshToken();
  const clientId = import.meta.env.VITE_TOKO_OAUTH_CLIENT_ID?.trim();
  if (!refresh || !clientId) return "";

  const body = new URLSearchParams();
  body.set("grant_type", "refresh_token");
  body.set("client_id", clientId);
  body.set("refresh_token", refresh);

  let data: Record<string, unknown> = {};
  let ok = false;
  if (typeof window !== "undefined" && "__TAURI_INTERNALS__" in window) {
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const raw = await invoke<string>("oauth_exchange_token", {
        tokenUrl: oauthTokenUrl(),
        body: body.toString(),
      });
      data = JSON.parse(raw) as Record<string, unknown>;
      ok = true;
    } catch {
      clearOAuthAccessToken();
      return "";
    }
  } else {
    const res = await fetch(oauthTokenUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
    data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    ok = res.ok;
  }
  if (!ok) {
    clearOAuthAccessToken();
    return "";
  }
  const access = typeof data.access_token === "string" ? data.access_token.trim() : "";
  if (!access) {
    clearOAuthAccessToken();
    return "";
  }
  persistOAuthTokenResponse({
    access_token: access,
    expires_in: Number(data.expires_in),
    refresh_token: typeof data.refresh_token === "string" ? data.refresh_token : refresh,
  });
  return access;
}

/** Ensure a usable access token, refreshing when near expiry. */
export async function ensureFreshOAuthAccessToken(): Promise<string> {
  const stored = readStoredAccess();
  if (!stored) return "";
  const exp = accessExpiresAtMs();
  if (!exp || Date.now() < exp - SKEW_MS) {
    window.__TOKO_POS_EMBED_BEARER__ = stored;
    return stored;
  }
  if (!refreshInflight) {
    refreshInflight = refreshOAuthAccessToken().finally(() => {
      refreshInflight = null;
    });
  }
  return refreshInflight;
}

export function scheduleOAuthRefresh(): void {
  if (refreshTimer != null) {
    window.clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  const exp = accessExpiresAtMs();
  if (!exp || !getOAuthRefreshToken()) return;
  const delay = Math.max(5_000, exp - Date.now() - SKEW_MS);
  refreshTimer = window.setTimeout(() => {
    void ensureFreshOAuthAccessToken();
  }, delay);
}

export function hydrateOAuthBearerOnWindow(): string {
  const token = getOAuthAccessToken();
  if (token) {
    window.__TOKO_POS_EMBED_BEARER__ = token;
    scheduleOAuthRefresh();
    return token;
  }
  // Expired access but refresh may still work — kick off async refresh for next call.
  if (readStoredAccess() && getOAuthRefreshToken()) {
    void ensureFreshOAuthAccessToken();
  }
  return "";
}

export function clearOAuthAccessToken(): void {
  if (refreshTimer != null) {
    window.clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  sessionStorage.removeItem(K_ACCESS);
  sessionStorage.removeItem(K_REFRESH);
  sessionStorage.removeItem(K_ACCESS_EXPIRES_AT);
  if (window.__TOKO_POS_EMBED_BEARER__) {
    delete window.__TOKO_POS_EMBED_BEARER__;
  }
}

export function setOAuthReturnPath(path: string): void {
  const p = path.startsWith("/") ? path : `/${path}`;
  sessionStorage.setItem(K_RETURN, p);
}

export function consumeOAuthReturnPath(): string {
  const path = sessionStorage.getItem(K_RETURN) ?? "/";
  sessionStorage.removeItem(K_RETURN);
  return path.startsWith("/") ? path : `/${path}`;
}
